import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getGlobalTopFans,
  topFanLevels,
  topFanScoreActions,
  topFanDashboardStats,
  criteriaWeights,
  getLevelByScore,
  getLevelProgress,
  formatTopFanCount,
  type TopFanEntry,
} from '@/mocks/rfwTopFans';

function FanCard({ fan, size = 'md' }: { fan: TopFanEntry; size?: 'lg' | 'md' | 'sm' }) {
  const level = getLevelByScore(fan.totalScore);
  const progress = getLevelProgress(fan);

  if (size === 'sm') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200">
        <div className="relative shrink-0">
          <img src={fan.photoUrl} alt={fan.name} className="w-10 h-10 rounded-full object-cover object-top" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${level.bgColor} ${level.textColor} border-2 border-background-50`}>
            {fan.globalRank}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground-950 truncate">{fan.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[9px] font-medium ${level.textColor}`}>{level.emoji} {level.name}</span>
            <span className="text-[9px] text-foreground-400">{fan.radioFrequency}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-foreground-950">{fan.totalScore}</p>
          <p className="text-[9px] text-foreground-400">pts</p>
        </div>
      </div>
    );
  }

  if (size === 'lg') {
    return (
      <div className={`rounded-2xl border overflow-hidden transition-all duration-300 hover:border-primary-200/60 ${fan.globalRank <= 3 ? 'bg-background-50 border-primary-200/40' : 'bg-background-50 border-background-200/70'}`}>
        <div className="p-5 text-center">
          <div className="relative inline-block mb-3">
            <img src={fan.photoUrl} alt={fan.name} className={`w-20 h-20 rounded-2xl object-cover object-top border-2 ${fan.globalRank === 1 ? 'border-amber-400' : fan.globalRank === 2 ? 'border-slate-300' : fan.globalRank === 3 ? 'border-orange-400' : 'border-background-200'}`} />
            <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-extrabold text-background-50 ${fan.globalRank === 1 ? 'bg-amber-500' : fan.globalRank === 2 ? 'bg-slate-500' : fan.globalRank === 3 ? 'bg-orange-500' : 'bg-foreground-700'}`}>
              #{fan.globalRank}
            </div>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-0.5">{fan.name}</p>
          <p className="text-[10px] text-foreground-500 mb-2">{fan.username} · {fan.radioFrequency}</p>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${level.bgColor} ${level.textColor}`}>
            {level.emoji} {level.name}
          </span>
          <div className="mt-3 text-center">
            <p className={`text-xl font-extrabold ${level.color}`}>{fan.totalScore}</p>
            <p className="text-[9px] text-foreground-400">puntos</p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1 text-center">
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{fan.escuchas}h</p>
              <p className="text-[8px] text-foreground-400">Escuchas</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{fan.comentarios}</p>
              <p className="text-[8px] text-foreground-400">Coment.</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{fan.participaciones}</p>
              <p className="text-[8px] text-foreground-400">Partic.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200">
      <div className="relative shrink-0">
        <img src={fan.photoUrl} alt={fan.name} className="w-11 h-11 rounded-xl object-cover object-top" />
        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${fan.globalRank <= 3 ? 'bg-amber-500 text-white' : 'bg-foreground-700 text-background-50'}`}>
          {fan.globalRank}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-semibold text-foreground-950 truncate">{fan.name}</p>
          <span className={`text-[9px] font-medium ${level.textColor}`}>{level.emoji}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-foreground-500">{fan.radioFrequency}</span>
          <span className="text-[9px] text-foreground-400">· {fan.escuchas}h · {fan.streak}d racha</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-bold ${level.color}`}>{fan.totalScore}</p>
      </div>
    </div>
  );
}

function LevelCard({ level, isActive, count }: { level: typeof topFanLevels[0]; isActive: boolean; count: number }) {
  return (
    <div className={`p-4 rounded-xl border transition-all duration-200 ${isActive ? 'bg-background-50 border-primary-300/60 ring-1 ring-primary-200/40' : 'bg-background-50 border-background-200/70 opacity-75 hover:opacity-100'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${level.bgColor}`}>
          <span className="text-lg">{level.emoji}</span>
        </div>
        <div>
          <p className={`text-xs font-bold ${level.textColor}`}>{level.name}</p>
          <p className="text-[9px] text-foreground-500">{level.minScore}-{level.maxScore} pts · {count} fans</p>
        </div>
      </div>
      <p className="text-[10px] text-foreground-600 leading-relaxed mb-2">{level.description}</p>
      <div className="space-y-1">
        {level.benefits.map((benefit, bi) => (
          <div key={bi} className="flex items-center gap-1.5">
            <i className={`${level.benefitIcons[bi] || 'ri-check-line'} text-[10px] ${level.textColor}`}></i>
            <span className="text-[10px] text-foreground-600">{benefit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TopFansGlobalesSection() {
  const [activeTab, setActiveTab] = useState<'global' | 'niveles' | 'beneficios'>('global');
  const globalFans = getGlobalTopFans(10);

  const tabs = [
    { id: 'global' as const, label: 'Top Fans Globales', icon: 'ri-global-line' },
    { id: 'niveles' as const, label: 'Niveles', icon: 'ri-stack-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-gift-2-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
            SISTEMA DE TOP FANS RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Los Más <span className="text-orange-600">Activos</span> de Cada Comunidad
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Escuchas, Comentarios, Saludos, Canciones, Compartidos y Participaciones. Cada acción te acerca al siguiente nivel: Fan → Top Fan → VIP → Embajador.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Top Fans', value: topFanDashboardStats.totalTopFans.toString(), icon: 'ri-star-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Embajadores', value: topFanDashboardStats.embajadores.toString(), icon: 'ri-vip-crown-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'VIPs', value: topFanDashboardStats.vips.toString(), icon: 'ri-vip-diamond-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Radios Activas', value: topFanDashboardStats.activeRadios.toString(), icon: 'ri-radio-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Score Generado', value: formatTopFanCount(topFanDashboardStats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'global' && (
          <div className="space-y-6">
            {/* Podium Global Top 3 */}
            <div className="flex items-end justify-center gap-3 md:gap-5 px-2 mb-8">
              {globalFans[1] && (
                <FanCard fan={globalFans[1]} size="lg" />
              )}
              {globalFans[0] && (
                <FanCard fan={globalFans[0]} size="lg" />
              )}
              {globalFans[2] && (
                <FanCard fan={globalFans[2]} size="lg" />
              )}
            </div>

            {/* Rest 4-10 */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-list-check text-xs text-foreground-500"></i>
                </div>
                <p className="text-xs font-bold text-foreground-700 uppercase tracking-wider">Posiciones 4 - 10</p>
              </div>
              {globalFans.slice(3, 10).map((fan) => (
                <FanCard key={fan.id} fan={fan} size="sm" />
              ))}
            </div>

            {/* Criterios de puntuación */}
            <div className="mt-8 p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-scales-line text-orange-500"></i>
                ¿Cómo se calcula el Score de Top Fan?
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {criteriaWeights.map((c) => (
                  <div key={c.key} className="text-center p-3 rounded-xl bg-background-100">
                    <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-2`}>
                      <i className={`${c.icon} ${c.color} text-sm`}></i>
                    </div>
                    <p className="text-[10px] font-bold text-foreground-950">{c.label}</p>
                    <p className="text-[9px] text-foreground-500">{(c.weight * 100).toFixed(0)}%</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'niveles' && (
          <div className="space-y-6">
            {/* Level Progression Ladder */}
            <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
              <div className="flex items-center justify-between gap-2 mb-6">
                {topFanLevels.map((level, idx) => (
                  <div key={level.id} className="flex-1 flex flex-col items-center gap-1.5">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${level.bgColor}`}>
                      <span className="text-base">{level.emoji}</span>
                    </div>
                    <p className={`text-[9px] font-bold ${level.textColor}`}>{level.name}</p>
                    <p className="text-[8px] text-foreground-400">{level.minScore}+</p>
                    {idx < topFanLevels.length - 1 && (
                      <div className="w-full h-0.5 rounded-full bg-background-200 mt-1"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Level Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {topFanLevels.map((level) => {
                const count = topFanLevels.filter((l) => l.id === level.id).length;
                return (
                  <LevelCard
                    key={level.id}
                    level={level}
                    isActive={level.id === 2}
                    count={topFanDashboardStats.topFansLevel}
                  />
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'beneficios' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { title: 'Insignias', desc: 'Badges visuales que muestran tu nivel y logros en el perfil y el chat.', icon: 'ri-award-line', levels: 'Todos los niveles', color: 'bg-amber-100', textColor: 'text-amber-700', iconColor: 'text-amber-600' },
                { title: 'Reconocimiento', desc: 'Menciones al aire, en rankings y en redes sociales de RFW.', icon: 'ri-megaphone-line', levels: 'Top Fan, VIP, Embajador', color: 'bg-orange-100', textColor: 'text-orange-700', iconColor: 'text-orange-600' },
                { title: 'Acceso Anticipado', desc: 'Entrada prioritaria a eventos, concursos y contenido exclusivo.', icon: 'ri-ticket-2-line', levels: 'Top Fan, VIP, Embajador', color: 'bg-violet-100', textColor: 'text-violet-700', iconColor: 'text-violet-600' },
                { title: 'Premios', desc: 'Regalos físicos y digitales: merch RFW, entradas, suscripciones premium.', icon: 'ri-gift-2-line', levels: 'VIP, Embajador', color: 'bg-rose-100', textColor: 'text-rose-700', iconColor: 'text-rose-600' },
              ].map((benefit) => (
                <div key={benefit.title} className="p-5 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-12 h-12 rounded-2xl ${benefit.color} flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${benefit.icon} ${benefit.iconColor} text-lg`}></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-950 mb-1">{benefit.title}</h4>
                  <p className="text-[10px] text-foreground-500 leading-relaxed mb-3">{benefit.desc}</p>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-medium ${benefit.color} ${benefit.textColor}`}>
                    {benefit.levels}
                  </span>
                </div>
              ))}
            </div>

            {/* Score Actions */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-flashlight-line text-orange-500"></i>
                Acciones que Suman Puntos
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {topFanScoreActions.slice(0, 6).map((action) => (
                  <div key={action.key} className={`flex items-center gap-2.5 p-3 rounded-xl ${action.bgColor}`}>
                    <i className={`${action.icon} ${action.color} text-sm`}></i>
                    <div>
                      <p className="text-[10px] font-bold text-foreground-950">+{action.points} pts</p>
                      <p className="text-[9px] text-foreground-500">{action.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/top-fans"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-star-line"></i>
            Ver Top Fans Completos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-orange-100/70 border border-orange-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-star-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Cada radio debe tener su Top Fans.
          </p>
          <p className="text-xs text-foreground-600">
            No existe una sola comunidad RFW — cada radio, cada programa, cada locutor construye la suya. Los Top Fans son el corazón de cada comunidad. Identificarlos, reconocerlos y premiarlos es la base del crecimiento orgánico de toda la plataforma.
          </p>
        </div>
      </div>
    </section>
  );
}